import { useAuth0 } from '@auth0/auth0-react';
import { SignOutIcon } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { markRedirectingToLogin } from 'api/accessToken';
import { getUser } from 'api/auth/user';
import { setupAxiosInterceptors } from 'api/interceptor/axiosClient';
import type { AxiosError } from 'axios';
import { DotLoader } from 'components/DotLoader';
import { OAUTH_ROUTE_PREFIX } from 'constants/constants';
import { AuthErrorPage } from 'pages/AuthErrorPage/AuthErrorPage';
import s from 'pages/AuthErrorPage/AuthErrorPage.module.scss';
import { OrgDisabledPage } from 'pages/OrgDisabledPage';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { setCurrentUserId, setRole, setUser } from 'redux/slices/authSlice';
import { useAppDispatch } from 'redux/store';
import { Button } from 'ui-library/components/Button/Button';
import { errorHandler } from 'utils/errorHandler';
import { resetReduxState } from 'utils/reduxReset';
import type { USER_ROLES } from 'utils/roles/types';

const baseRolesUrl = import.meta.env.VITE_AUTH_NAMESPACE as string;

const AuthHandler: FC<PropsWithChildren> = ({ children }) => {
  'use memo';

  const dispatch = useAppDispatch();
  const { isAuthenticated, getAccessTokenSilently, user, isLoading, loginWithRedirect, logout } = useAuth0();
  const [searchParams] = useSearchParams();
  // Third-party OAuth callback routes get their own ?error= from that provider,
  // not from Auth0 - it must not be treated as our own auth error.
  const isOAuthPopupRoute = window.location.pathname.startsWith(OAUTH_ROUTE_PREFIX);
  const error =
    !isOAuthPopupRoute && searchParams.has('error')
      ? (searchParams.get('error_description') ?? searchParams.get('error') ?? 'Unknown error')
      : undefined;

  const isOrgDisabled = useMemo(
    () => user != null && String(user[`${baseRolesUrl}/ankar-org-enabled`]) === 'false',
    [user],
  );

  const redirectToLogin = useCallback(() => {
    markRedirectingToLogin();
    resetReduxState(dispatch);
    void loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
      openUrl: url => {
        window.location.assign(url);
      },
    });
  }, [loginWithRedirect, dispatch]);

  useEffect(() => {
    if (!error && !isLoading && !isAuthenticated) {
      redirectToLogin();
    }
  }, [isLoading, isAuthenticated, redirectToLogin, searchParams, error]);

  useEffect(() => {
    if (!error) {
      setupAxiosInterceptors({ type: 'auth', getAccessTokenSilently, redirectToLogin });
    }
  }, [error, getAccessTokenSilently, redirectToLogin]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (isAuthenticated) {
        if (user) {
          dispatch(setRole(user[`${baseRolesUrl}/roles`] as USER_ROLES[]));
          dispatch(setCurrentUserId(user[`${baseRolesUrl}/ankar-user-id`] as string | null));
        }
        if (isOrgDisabled) {
          return;
        }
        try {
          const userInfo = await getUser();
          dispatch(setUser(userInfo));
          Sentry.setUser({ id: userInfo.userId });
        } catch (error: unknown) {
          errorHandler(error as AxiosError);
        }
      }
    };

    void fetchUserInfo();
  }, [isAuthenticated, user, dispatch, isOrgDisabled]);

  if (error) {
    return (
      <AuthErrorPage
        error={error}
        showHeader
        action={
          <Button
            className={s.logoutButton}
            variant="secondary-dark"
            startAdornment={<SignOutIcon />}
            onClick={() => {
              resetReduxState(dispatch);
              void logout({
                logoutParams: {
                  returnTo: window.location.origin,
                },
              });
            }}
          >
            Log out
          </Button>
        }
      />
    );
  }

  if (isLoading || !isAuthenticated) {
    return <DotLoader fullScreen />;
  }

  if (isOrgDisabled) {
    return <OrgDisabledPage />;
  }

  return children;
};

export default AuthHandler;
